/** Перенос `domain/models/config_model/config_model.dart`. */

import { z } from 'zod';

import { personModelSchema, personModelToJson, type PersonModel } from '@/domain/models/personModel';
import {
  updateConfigModelSchema,
  updateConfigModelToJson,
  type UpdateConfigModel,
} from '@/domain/models/updateConfigModel';
import { type Nullable, resolveNullable } from '@/utils/nullable';

export interface ConfigModel {
  /**
   * Общий адрес распознавания речи. Новый бекенд его не отдаёт —
   * адрес задаётся у аватара в `stt`.
   */
  readonly transriptionsUrl: string | null;
  readonly persons: readonly PersonModel[];
  readonly updateConfig: UpdateConfigModel | null;
}

/**
 * Ключи `transcribe_url` и `app` разбираются «как есть», `transform`
 * переименовывает их в поля модели (`@JsonKey` в Dart). Опечатка
 * `transriptionsUrl` оставлена намеренно — так поле зовётся в оригинале.
 */
const configModelRawSchema = z.object({
  transcribe_url: z.string().nullish(),
  persons: z.array(personModelSchema),
  app: updateConfigModelSchema.nullish(),
});

export const configModelSchema = configModelRawSchema.transform(
  (raw): ConfigModel => ({
    transriptionsUrl: raw.transcribe_url ?? null,
    persons: raw.persons,
    updateConfig: raw.app ?? null,
  }),
);

export function configModelFromJson(json: unknown): ConfigModel {
  return configModelSchema.parse(json);
}

export function configModelToJson(model: ConfigModel): Record<string, unknown> {
  return {
    transcribe_url: model.transriptionsUrl,
    persons: model.persons.map((person) => personModelToJson(person)),
    app: model.updateConfig === null ? null : updateConfigModelToJson(model.updateConfig),
  };
}

/**
 * Патч для [configModelCopyWith]. Обнуляемые поля идут в `Nullable`:
 * без обёртки поле не трогается, `new Nullable(null)` стирает его — так же
 * работал sentinel `freezed` в Dart. Обязательный `persons` обнулить нельзя,
 * поэтому обёртка ему не нужна.
 */
export interface ConfigModelPatch {
  readonly transriptionsUrl?: Nullable<string | null>;
  readonly persons?: readonly PersonModel[];
  readonly updateConfig?: Nullable<UpdateConfigModel | null>;
}

/** `ConfigModel.copyWith` — всегда новый объект, исходный не меняется. */
export function configModelCopyWith(model: ConfigModel, patch: ConfigModelPatch): ConfigModel {
  return {
    transriptionsUrl: resolveNullable(patch.transriptionsUrl, model.transriptionsUrl),
    persons: patch.persons ?? model.persons,
    updateConfig: resolveNullable(patch.updateConfig, model.updateConfig),
  };
}
